import { reactive, computed } from 'vue';
import { supabase, supabaseUrl, supabaseAnonKey } from '../supabase.js';
import { coreState } from './core.js';
import { Profile } from '../models/Profile.js';
import { Shop } from '../models/Shop.js';
import { ShopMember } from '../models/ShopMember.js';
import { LanguageManager } from '../models/LanguageManager.js';
import { coordinateUtils } from '../utils/coordinateUtils.js';
import { MARKETING_BASE_URL } from '../constants.js';

// Simple obfuscation to prevent generic malware sweeps from finding plaintext JWTs
const OFFLINE_KEY = '__lwt_osess';
const OFFLINE_PROFILE_KEY = '__lwt_oprof';
const OFFLINE_CONTEXT_KEY = '__lwt_octxt';

function encodeSession(session) {
    if (!session) return null;
    try {
        const json = JSON.stringify(session);
        return btoa(unescape(encodeURIComponent(json)));
    } catch (e) { return null; }
}

function decodeSession(str) {
    if (!str) return null;
    try {
        const json = decodeURIComponent(escape(atob(str)));
        return JSON.parse(json);
    } catch (e) { return null; }
}

function _readRawGoTrueSession() {
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
                const raw = localStorage.getItem(key);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    // GoTrue stores as { currentSession: {...}, expiresAt: ... }
                    return parsed?.currentSession || parsed;
                }
            }
        }
    } catch (e) { }
    return null;
}

// Independent State for Auth
const authState = reactive({
    session: null,
    user: null, // The Supabase User object
    profile: null, // The App Profile object
    isReady: false, // Has initial check finished?
    isLoading: false,
    error: null,
    lastEventTimestamp: 0 // [NEW] Track last auth event for debounce
});

// [CRITICAL] Expose authState for the _getAccessToken patch in supabase.js
// This allows DB calls to read the access token from memory instead of
// calling getSession() which hangs after Chrome passive state.
window.__authState = authState;

// [Concurrency Lock]
let refreshPromise = null;
let isRestoring = false; // [NEW] Flag to prevent loop during init
let authoritativeUserId = null; // [BUG FIX] Guard against stale OAuth session race

export function useAuth() {

    // Helper for Toast (Safe access)
    const _toast = (msg, type = 'error') => {
        if (coreState.showToast) {
            coreState.showToast(msg, type);
        } else {
            console.warn("[Auth] Toast not available:", msg);
            // Fallback manual manipulation if needed, or rely on console
            coreState.toast.message = msg;
            coreState.toast.type = type;
            coreState.toast.visible = true;
            setTimeout(() => coreState.toast.visible = false, 3000);
        }
    };

    // --- Actions ---

    async function initAuth() {
        if (authState.isReady) return;

        // 1. Setup Listener First (to catch any immediate events or those triggered by checks)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log(`[Auth] Event: ${event}`);

            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                if (session) {
                    // [BUG FIX] Reject stale sessions from a different user.
                    // This prevents a cached OAuth session in localStorage from being
                    // auto-refreshed and overwriting the correct edge cookie session
                    // (e.g., user logged in with email/password but a stale Google
                    // OAuth session races and wins).
                    if (authoritativeUserId && session.user?.id !== authoritativeUserId) {
                        console.warn(`[Auth] Rejected stale session for user ${session.user?.id}. Authoritative user is ${authoritativeUserId}. Ignoring.`);
                        return;
                    }

                    // [PWA FIX] Update local offline cache FIRST
                    await _handleSessionUpdate(session);

                    // Sync to Cookie unless we are in the middle of restoring it
                    if (!isRestoring) {
                        _syncToCookie(session).catch(e => console.warn("[AuthDebug] Non-blocking cookie sync failed:", e));
                    }
                } else {
                    await _handleSessionUpdate(session);
                }
            } else if (event === 'SIGNED_OUT') {
                // [OFFLINE-FIRST] GoTrue's SIGNED_OUT can fire from a transient refresh
                // failure. Preserve data caches so revival or re-login rehydrates
                // instantly. Manual logout already calls _clearState() upstream.
                _clearSessionOnly();
                if (!isRestoring) {
                    _syncToCookie(null).catch(e => console.warn("[AuthDebug] Non-blocking cookie clear failed:", e));
                }
            }
        });

        // Check for OAuth callback in URL (Hash Fragment)
        const isOAuthCallback = window.location.hash &&
            (window.location.hash.includes('access_token') ||
                window.location.hash.includes('refresh_token') ||
                window.location.hash.includes('error_description'));

        if (isOAuthCallback) {
            console.log("[Auth] OAuth callback detected. Waiting for Supabase to process...");

            // Wait for the SIGNED_IN event to fire (handled by listener above)
            // or timeout if it fails (e.g. error in hash).
            await new Promise(resolve => {
                const checkInterval = setInterval(() => {
                    if (authState.session || authState.error) {
                        clearInterval(checkInterval);
                        resolve();
                    }
                }, 100);

                // Fallback Timeout (3s) - if Supabase fails to parse, we shouldn't hang forever
                setTimeout(() => {
                    clearInterval(checkInterval);
                    console.warn("[Auth] OAuth processing timed out. Proceeding...");
                    resolve();
                }, 3000);
            });

            authState.isReady = true;

        } else {
            console.log("[Auth] Initializing via Edge Function Cookie...");
            isRestoring = true; // Block sync during restore

            // [FIX] If offline, we can't reach the Edge Function. Read local session instead.
            if (!navigator.onLine) {
                console.log("[Auth] Offline boot. Attempting local session restore...");
                try {
                    // Try our manual offline session first to fully bypass gotrue's hang
                    const offlineStr = localStorage.getItem(OFFLINE_KEY);
                    let offlineSession = decodeSession(offlineStr);
                    
                    if (!offlineSession) {
                        offlineSession = _readRawGoTrueSession();
                    }

                    if (offlineSession) {
                        console.log("[Auth] Restored offline session from local cache.");
                        await _handleSessionUpdate(offlineSession);
                    } else {
                        console.log("[Auth] No offline session found. Remaining logged out.");
                        await _handleSessionUpdate(null);
                    }
                } catch (e) {
                    await _handleSessionUpdate(null);
                } finally {
                    isRestoring = false;
                    authState.isReady = true;
                }
                return;
            }

            try {
                // 2. Ask Edge Function for Session
                // This replaces LocalStorage + Bridge + Locks
                const { data, error } = await supabase.functions.invoke('fast_cookie', {
                    method: 'GET'
                });

                if (error) {
                    // [LIE-FI AWARENESS] The server might have returned an error, or the fetch failed (Workbox block/timeout).
                    const isNetworkError =
                        error.message?.includes('Failed to fetch') ||
                        error.message?.includes('NetworkError') ||
                        error.name === 'TypeError' ||
                        error.code === 'FetchError'; // Supabase sometimes uses this

                    if (isNetworkError) {
                        console.warn("[Auth] fast_cookie fetch failed (network issue), trusting local session.", error);
                        // Fallback to local session instead of logging out
                        let fallbackSession = null;
                        try {
                            const offlineStr = localStorage.getItem(OFFLINE_KEY);
                            fallbackSession = decodeSession(offlineStr);
                        } catch (e) { }

                        await _handleSessionUpdate(fallbackSession);
                    } else {
                        // Real error from edge function (e.g. 401 Unauthorized)
                        throw error;
                    }
                } else if (data && data.session) {
                    // [REVIVAL FIX] Check if our local offline cache has a newer session than the Edge Cookie
                    // This happens if the PWA was background-killed before the Edge Cookie sync completed.
                    let sessionToApply = data.session;
                    
                    try {
                        const offlineStr = localStorage.getItem(OFFLINE_KEY);
                        let offlineSession = decodeSession(offlineStr);
                        
                        // Try raw GoTrue cache if OFFLINE_KEY is empty/invalid
                        if (!offlineSession) {
                            offlineSession = _readRawGoTrueSession();
                        }
                        
                        if (offlineSession && offlineSession.expires_at && data.session.expires_at) {
                            // If local cache expires later than the edge cookie, it's a newer token!
                            if (offlineSession.expires_at > data.session.expires_at) {
                                console.warn("[AuthDebug] Local cache has a NEWER token than Edge Cookie. Reviving stale edge cookie.");
                                sessionToApply = offlineSession;
                                
                                // Silently trigger a background sync to fix the stale edge cookie
                                _syncToCookie(offlineSession).catch(e => console.warn("[AuthDebug] Silent edge cookie sync failed:", e));
                            }
                        }
                    } catch (e) {
                        console.warn("[AuthDebug] Failed to parse offline cache during token comparison:", e);
                    }

                    console.log("[Auth] Session restored from Edge/Local.");

                    // [BUG FIX] Lock to this user ID. Any stale session events from
                    // a different user (e.g., auto-refreshed OAuth from localStorage)
                    // will be rejected by the onAuthStateChange guard above.
                    authoritativeUserId = sessionToApply.user?.id || null;

                    // [HYDRATION FIX] Manually update session FIRST to block initAuth and prevent router flash
                    await _handleSessionUpdate(sessionToApply);

                    // Set the session in memory (Supabase Client) -> Triggers SIGNED_IN
                    const { error: setErr } = await supabase.auth.setSession(sessionToApply);
                    if (setErr) throw setErr;
                } else {
                    console.log("[Auth] No session found on server.");
                    await _handleSessionUpdate(null);
                }

            } catch (err) {
                console.error("[Auth] Init failed:", err);
                
                // [PWA RECOVERY FIX] If setSession still fails with an 'Already Used' error,
                // it means both Edge Cookie and local offline cache are definitively stale.
                if (err.message?.includes('Already Used') || err.message?.includes('Invalid Refresh Token')) {
                    console.warn("[AuthDebug] Session token rejected as already used during boot.");
                }

                // If edge function explicitly fails (not network), we are logged out.
                await _handleSessionUpdate(null);
            } finally {
                isRestoring = false; // Unblock
                authState.isReady = true;
            }
        }
    }

    async function login(email, password) {
        authState.isLoading = true;
        authState.error = null;
        try {
            const { data, error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) throw error;

            // SYNC is now handled by onAuthStateChange('SIGNED_IN') automatically.

            console.log("[Auth] Login Successful.");
            _toast("Welcome back!", "success");
            return data;
        } catch (err) {
            console.error("[Auth] Login Failed:", err);
            authState.error = err;
            _toast(err.message || "Login failed", "error");
            throw err;
        } finally {
            authState.isLoading = false;
        }
    }


    // [Enhanced] Active Session Health Check
    async function ensureSession() {
        if (!authState.session) {
            console.warn("[AuthDebug] ensureSession: No local session found.");
            return null;
        }

        const expiresAt = authState.session.expires_at; // Seconds timestamp
        const now = Math.floor(Date.now() / 1000);
        const timeRemaining = expiresAt ? (expiresAt - now) : 0;

        // [FIX] Session already expired - attempt immediate refresh
        if (expiresAt && expiresAt < now) {
            console.warn(`[AuthDebug] Session expired (${timeRemaining}s ago). Attempting refresh...`);

            // [Concurrency Fix] Prevent thundering herd
            if (!refreshPromise) {
                // [LOCK FIX] Wrap in application-level timeout to prevent infinite hang
                const p = supabase.auth.refreshSession();
                const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("Refresh Timeout")), 5000));

                refreshPromise = Promise.race([p, timeout])
                    .finally(() => {
                        refreshPromise = null;
                    });
            }

            try {
                const { data, error } = await refreshPromise;

                if (error || !data.session) {
                    const isNetworkError =
                        error?.message?.includes('Failed to fetch') ||
                        error?.message?.includes('NetworkError') ||
                        error?.status === 0 || // 0 indicates request didn't complete
                        error?.code === 'FetchError';

                    if (isNetworkError) {
                        console.warn("[AuthDebug] Refresh failed due to network. Keeping expired session locally.", error);
                        _toast("Network unstable. Using offline session.", "warning");
                        return authState.session; // Trust local cache
                    }

                    // Strict check for actual invalid tokens, not just generic failures
                    const isDefinitiveLogout = error?.message?.includes('Invalid Refresh Token') ||
                        error?.message?.includes('Invalid grant') ||
                        error?.status === 400 ||
                        error?.status === 401;

                    if (isDefinitiveLogout) {
                        // [FIX] If online and server definitively rejected us, keeping the
                        // zombie session alive causes every API call to return empty data,
                        // overwriting cached shops/sites/feed with nothing.
                        if (navigator.onLine) {
                            console.error("[AuthDebug] Definitive auth rejection while ONLINE. Session is irrecoverable. Checking for cross-tab token refresh...");
                            
                            // [CROSS-TAB RECOVERY] Another tab or background process might have successfully refreshed it
                            try {
                                const offlineStr = localStorage.getItem(OFFLINE_KEY);
                                let offlineSession = decodeSession(offlineStr);
                                if (!offlineSession) offlineSession = _readRawGoTrueSession();
                                
                                if (offlineSession && offlineSession.access_token !== authState.session?.access_token) {
                                    const sessionNow = Math.floor(Date.now() / 1000);
                                    if (offlineSession.expires_at && offlineSession.expires_at > sessionNow) {
                                        console.log("[AuthDebug] Found a newer valid session in offline cache (from another tab). Recovering silently.");
                                        await _handleSessionUpdate(offlineSession);
                                        await supabase.auth.setSession(offlineSession);
                                        return offlineSession;
                                    }
                                }
                            } catch (e) {
                                console.warn("[AuthDebug] Cross-tab recovery check failed:", e);
                            }
                            
                            _toast("Your session has expired. Please log in again.", "warning");
                            await logoutAuto();
                            return null;
                        } else {
                            console.warn("[AuthDebug] Definitive auth rejection while OFFLINE. Preserving session for offline use.");
                            _toast("Session expired. Using offline data until you reconnect.", "warning");
                            return authState.session;
                        }
                    } else {
                        console.warn("[AuthDebug] Unknown refresh error, trusting local session to prevent fatal lockout.", error);
                        _toast("Trouble connecting to server. Using offline data.", "warning");
                        return authState.session;
                    }
                }
                console.log("[AuthDebug] Session successfully refreshed (Expired recovery).");
                await _handleSessionUpdate(data.session);
                return data.session;
            } catch (err) {
                console.warn("[AuthDebug] Refresh Promise Timeout/Error:", err);

                // [FIX] If the promise timed out, the gotrue lock is likely frozen by the PWA OS.
                // We bypass gotrue entirely and manually fetch the token.
                if (err.message === "Refresh Timeout" && authState.session?.refresh_token) {
                    console.warn("[AuthDebug] Gotrue lock stuck. Performing manual token refresh fallback...");
                    try {
                        const fallbackRes = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
                            method: 'POST',
                            headers: {
                                'apikey': supabaseAnonKey,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({ refresh_token: authState.session.refresh_token })
                        });

                        if (fallbackRes.ok) {
                            const data = await fallbackRes.json();
                            await supabase.auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token });
                            // This triggers onAuthStateChange which updates authState.session
                            return authState.session;
                        } else {
                            // Definitive failure from manual fetch
                            if (navigator.onLine) {
                                console.error("[AuthDebug] Fallback fetch definitive rejection while ONLINE. Logging out.");
                                _toast("Your session has expired. Please log in again.", "warning");
                                await logoutAuto();
                                return null;
                            } else {
                                console.warn("[AuthDebug] Fallback fetch definitive rejection while OFFLINE. Preserving session.");
                                _toast("Session expired. Using offline data until you reconnect.", "warning");
                                return authState.session;
                            }
                        }
                    } catch (fallbackErr) {
                        console.warn("[AuthDebug] Fallback fetch failed due to network. Trusting local session.");
                        _toast("Network unstable. Using offline session.", "warning");
                        return authState.session;
                    }
                }

                const isNetworkError =
                    err.message?.includes('Failed to fetch') ||
                    err.name === 'TypeError';

                if (isNetworkError) {
                    console.warn("[AuthDebug] Refresh attempt aborted due to network. Trusting local session.");
                    _toast("Network unstable. Using offline session.", "warning");
                    return authState.session;
                }

                console.warn("[AuthDebug] Unexpected refresh error. Trusting local session as a failsafe.", err);
                return authState.session;
            }
        }

        // Expiring soon (< 5 minutes) - proactively refresh
        if (expiresAt && (expiresAt - now < 300)) {
            console.log(`[AuthDebug] Token expiring soon (${timeRemaining}s). Refreshing...`);
            const { data, error } = await supabase.auth.refreshSession();
            if (error) {
                console.error("[AuthDebug] Proactive Refresh Failed:", error);
                // Don't logout immediately on proactive fail, purely network? 
                // But if it fails, we might be in trouble soon.
                // For now, let it slide until hard expiry or next API call fails.
                return authState.session; // Return current session (better than null if just net blip)
            }
            if (data.session) {
                console.log("[AuthDebug] Proactive Refresh Success.");
                await _handleSessionUpdate(data.session);
                return data.session;
            }
        }
        return authState.session;
    }

    // [NEW] Helper for stores to handle RLS/Auth errors
    function handleAuthError(error) {
        if (!error) return false;

        const isDefinitiveAuthError =
            error.code === 'PGRST301' || // JWT expired and couldn't be refreshed
            error.code === '401' ||
            error.code === '406' || // Not Acceptable
            (error.message && (error.message.includes('invalid_grant') || error.message.includes('Invalid Refresh Token')));

        // Only log out on definitive server rejections in strictly online flows
        // For general API calls, we prefer graceful degradation.
        if (isDefinitiveAuthError) {
            console.warn("[AuthDebug] Detected definitive auth error in API response. Gracefully degrading to offline mode.", error);
            _toast("Session expired. Falling back to offline mode. Please log in when connected.", "warning");
            // DO NOT LOGOUT here either, maintain the offline state barrier
            return true;
        }

        // Log but don't force logout for ambiguous errors
        if (error.message?.toLowerCase().includes('jwt') || error.message?.toLowerCase().includes('token')) {
            console.warn("[AuthDebug] Ambiguous token error detected, but keeping session alive.", error);
            return false;
        }

        return false;
    }

    // [OFFLINE-FIRST] Auto-failure logout: session is irrecoverable and the user
    // must re-authenticate, but they ARE still the same user. Preserves
    // OFFLINE_PROFILE_KEY and OFFLINE_CONTEXT_KEY so re-login rehydrates
    // instantly from cache. Use this from ensureSession/verifySession's
    // definitive-logout branches; use logout(true) only when the user
    // explicitly clicked logout (full wipe + sb-* keys destroyed).
    async function logoutAuto() {
        console.log("[Auth] Auto-logout: session expired/invalid, preserving data caches.");

        _clearSessionOnly();

        try {
            await _syncToCookie(null);
        } catch (e) {
            console.warn("[AuthDebug] Failed to clear edge cookie during auto-logout:", e);
        }

        try {
            await supabase.auth.signOut();
        } catch (e) {
            console.warn("[AuthDebug] Supabase signOut failed during auto-logout:", e);
        }

        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        if (isStandalone) {
            console.log("[Auth] PWA mode: redirecting to /start.");
            window.location.replace('/start');
        } else {
            console.log("[Auth] Browser mode: redirecting to marketing site.");
            window.location.replace(`${MARKETING_BASE_URL}/?session_expired=true`);
        }
    }

    async function logout(isManual = false) {
        console.log(`[Auth] Logging out... Manual: ${isManual}`);

        if (isManual) {
            console.log("[Auth] Forcing local state destruction for manual logout.");
            // 1. Immediately clear state to prevent UI hang if API calls fail
            _clearState();
        } else {
            console.log("[Auth] Preserving offline state for automatic/system logout.");
            // Just clear the memory session to prevent further API calls, but keep OFFLINE keys
            authState.session = null;
            authState.user = null;
            coreState.authSession = null;
        }

        // 2. Attempt server-side cleanup but DO NOT BLOCK on failure
        try {
            await _syncToCookie(null);
        } catch (e) {
            console.warn("[AuthDebug] Failed to clear edge cookie during logout:", e);
        }

        try {
            await supabase.auth.signOut();
        } catch (e) {
            console.warn("[AuthDebug] Supabase signOut failed (likely expired token), forcing local clear.", e);
        } finally {
            if (isManual) {
                // 3. Failsafe: Destroy any lingering Supabase auth keys in localStorage
                Object.keys(localStorage).forEach(key => {
                    if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
                        localStorage.removeItem(key);
                    }
                });
            }
        }

        // 4. Redirect
        if (isManual) {
            // Check if running as Installed PWA (Standalone/Fullscreen)
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

            if (isStandalone) {
                console.log("[Auth] PWA detected. Redirecting to app start screen.");
                window.location.replace('/start');
            } else {
                console.log("[Auth] Browser mode detected. Redirecting to marketing site.");
                window.location.replace(`${MARKETING_BASE_URL}/?logout=true`);
            }
        } else {
            console.log("[Auth] Automatic logout handled silently. Offline state preserved.");
        }
    }

    // New Helper: Sync to Edge Function
    async function _syncToCookie(session) {
        try {
            if (session) {
                await supabase.functions.invoke('fast_cookie', {
                    method: 'POST',
                    body: { session }
                });
            } else {
                await supabase.functions.invoke('fast_cookie', {
                    method: 'DELETE'
                });
            }
        } catch (e) {
            console.error("[Auth] Cookie Sync Failed:", e);
            // User might be logged in locally but not on server. High risk state.
        }
    }

    // --- Internals ---

    async function _handleSessionUpdate(session) {
        if (session && authState.session?.access_token === session.access_token) {
            // Already initialized for this exact token, prevent double firing during boot
            return;
        }

        authState.session = session;
        authState.user = session ? session.user : null;

        // Sync to Core (Legacy Support)
        coreState.authSession = session;

        if (session) {
            localStorage.setItem(OFFLINE_KEY, encodeSession(session));
            // [FIX] Background fetch — DO NOT await, so the app mounts instantly even if offline
            fetchUserProfile(session.user.id);
        } else {
            // [OFFLINE-FIRST] Preserve data caches on automatic clears (refresh failures,
            // catch-block recovery) so re-login or revival rehydrates without a flash of empty.
            _clearSessionOnly();
        }
    }

    function _clearState() {
        console.log("[AuthDebug] Clearing Auth State (full wipe).");
        localStorage.removeItem(OFFLINE_KEY);
        localStorage.removeItem(OFFLINE_PROFILE_KEY);
        localStorage.removeItem(OFFLINE_CONTEXT_KEY);
        authState.session = null;
        authState.user = null;
        authState.profile = null;

        // Sync to Core (Legacy Support)
        coreState.authSession = null;
        coreState.activeUser = null;
        coreState.members.clear();
        coreState.shops.clear();
        coreState.currentShopId = null;
        coreState.currentUserRole = null;
    }

    // [OFFLINE-FIRST] Auto-logout / refresh-failure path. Clears session
    // identity + in-memory data so the router can redirect, but PRESERVES
    // OFFLINE_PROFILE_KEY and OFFLINE_CONTEXT_KEY in localStorage so a
    // subsequent re-login or revival can rehydrate instantly. Only manual
    // logout (logout(true)) should call _clearState() and torch the data caches.
    function _clearSessionOnly() {
        console.log("[AuthDebug] Clearing session state (preserving data caches).");
        localStorage.removeItem(OFFLINE_KEY);
        authState.session = null;
        authState.user = null;
        authState.profile = null;

        coreState.authSession = null;
        coreState.activeUser = null;
        coreState.members.clear();
        coreState.shops.clear();
        coreState.currentShopId = null;
        coreState.currentUserRole = null;
    }

    async function fetchUserProfile(userId) {
        // [STALE-WHILE-REVALIDATE FIX] ALWAYS restore profile from cache synchronously first
        try {
            const cachedProfileStr = localStorage.getItem(OFFLINE_PROFILE_KEY);
            if (cachedProfileStr) {
                const parsed = decodeSession(cachedProfileStr);
                if (parsed && parsed.id === userId) {
                    const profile = new Profile(parsed);
                    authState.profile = profile;
                    coreState.activeUser = profile; // Fills memory before router runs
                    coreState.users.set(userId, profile);
                }
            }
        } catch (e) {
            console.warn("[AuthDebug] Failed to parse offline profile cache.", e);
        }

        if (!navigator.onLine) {
            if (!coreState.activeUser) {
                coreState.activeUser = { id: userId, name: 'Offline User', avatar: '' };
            }

            // [FIX] We must still cascade the hydration to the Context cache!
            await fetchUserContext(userId);
            return;
        }

        console.log(`[AuthDebug] Fetching profile for ${userId}...`);
        // 1. Try Fetching from DB (with Retry for Trigger Latency)
        let profileData = null;
        let attempts = 0;
        const maxAttempts = 3;

        while (attempts < maxAttempts && !profileData) {
            // [POISON GUARD] Abort if session was cleared mid-flight. Prevents a zombie
            // fetch from a prior session from repopulating state after _clearSessionOnly.
            if (authState.user?.id !== userId) {
                console.warn("[AuthDebug] fetchUserProfile aborted: session changed mid-flight.");
                return;
            }

            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (data) {
                profileData = data;
                break;
            }

            // [POISON GUARD] Stop retrying on auth errors — retrying the same dead JWT
            // is futile and burns time the user is staring at "Profile not found".
            const isAuthError = error?.code === 'PGRST301' ||
                error?.code === '401' ||
                error?.status === 401 ||
                error?.message?.toLowerCase().includes('jwt');
            if (isAuthError) {
                console.warn("[AuthDebug] fetchUserProfile aborted: auth error during retry.", error);
                return;
            }

            // Wait 500ms before retry
            attempts++;
            if (attempts < maxAttempts) {
                console.log(`[AuthDebug] Profile not found yet. Retrying (${attempts}/${maxAttempts})...`);
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        if (profileData) {
            const profile = new Profile({
                id: profileData.id,
                email: profileData.email,
                name: profileData.name,
                avatar: profileData.avatar,
                languages: profileData.languages,
                signupCompleted: profileData.signup_completed,
                onboarding_intent: profileData.onboarding_intent,
                qualifications: profileData.qualifications || [],
                specialties: profileData.specialties || [],
                stats: profileData.stats || { totalDives: 0 },
                historySummary: profileData.history_summary || { totalLogs: 0 }
            });

            // Sync Avatar from Auth Metadata if missing in Profile
            _syncAvatar(userId, profile);

            // [IMPORTANT] Hydrate Shops & Memberships BEFORE setting profile
            // This ensures watchers (like SignUpFlow) see populated coreState
            await fetchUserContext(userId);

            // [POISON GUARD] If the session was cleared while fetchUserContext awaited,
            // do NOT write profile back into authState/coreState/localStorage —
            // that would resurrect identity for a session that no longer exists.
            if (authState.user?.id !== userId) {
                console.warn("[AuthDebug] fetchUserProfile aborted before write-back: session changed.");
                return;
            }

            // NOW set profile - watchers will fire with context ready
            authState.profile = profile;
            coreState.activeUser = profile; // Sync Legacy
            coreState.users.set(userId, profile); // Sync Legacy Map

            // Dump profile into offline cache stringified
            try {
                localStorage.setItem(OFFLINE_PROFILE_KEY, encodeSession(profile));
            } catch (e) { }

        } else {
            console.warn("[AuthDebug] Profile not found after retries. Verifying user existence...");

            // [SECURITY FIX] Verify if the user actually exists on the server.
            // A local JWT might still be valid (expiry) even if the user was deleted/banned.
            const { data: userData, error: userError } = await supabase.auth.getUser();

            if (userError) {
                console.warn("[AuthDebug] User verification failed (Network/Unknown). Continuing with local session.", userError);
                // Assume valid if we have a local session but network failed
            } else if (!userData.user) {
                console.error("[AuthDebug] User verification failed (Deleted/Invalid). Logging out.");
                _toast("Account no longer exists. Logging out.", "error");
                await logout();
                return;
            }

            console.log("[AuthDebug] User verified on server. Creating draft profile...");
            // Only create draft if we REALLY think it's a new user flow that failed DB creation?
            // Or usually this means just display "Finish Setup".
            _createDraftProfile(userId);

            // [FIX] Ensure we still attempt to hydrate the Context cache!
            await fetchUserContext(userId);
        }
    }

    async function fetchUserContext(userId) {
        // [STALE-WHILE-REVALIDATE FIX] ALWAYS restore context from cache synchronously first
        try {
            const cachedContextStr = localStorage.getItem(OFFLINE_CONTEXT_KEY);
            if (cachedContextStr) {
                const parsedContext = decodeSession(cachedContextStr);
                if (parsedContext && parsedContext.userId === userId) {
                    // Hydrate Shops
                    if (parsedContext.shops) {
                        parsedContext.shops.forEach(s => coreState.shops.set(s.id, new Shop(s)));
                    }
                    // Hydrate Memberships
                    if (parsedContext.members) {
                        parsedContext.members.forEach(m => coreState.members.set(m.id, new ShopMember(m)));
                    }

                    // Hydrate Active IDs
                    if (parsedContext.currentShopId) coreState.currentShopId = parsedContext.currentShopId;
                    if (parsedContext.currentUserRole) coreState.currentUserRole = parsedContext.currentUserRole;

                    // Support fallback if cache was corrupted/empty but we still have members
                    if (!coreState.currentShopId && coreState.members.size > 0) {
                        const activeMembers = Array.from(coreState.members.values()).filter(m => m.status && m.status.toLowerCase() === 'active');
                        if (activeMembers.length > 0) {
                            coreState.currentShopId = activeMembers[0].shopId;
                            coreState.currentUserRole = activeMembers[0].role;
                            localStorage.setItem('lewotobi_shop_id', coreState.currentShopId);
                        }
                    }

                    console.log(`[AuthDebug] Restored offline context for ${userId}. Shops: ${coreState.shops.size}, Members: ${coreState.members.size}`);
                }
            }
        } catch (e) {
            console.warn("[AuthDebug] Failed to parse offline context cache.", e);
        }

        if (!navigator.onLine) {
            console.log(`[AuthDebug] Offline. Skipping fetchUserContext for ${userId}, relying on cache.`);
            return;
        }
        // Copied & Adapted from legacy store.js
        console.log(`[AuthDebug] Fetching memberships for ${userId}...`);
        const { data: members, error } = await supabase
            .from('shop_members')
            .select(`*, shop:shops(*)`)
            .eq('user_id', userId);

        // [POISON GUARD] Abort if session was cleared while the fetch was in flight.
        // Without this, a zombie fetch from a now-dead session writes empty data
        // into coreState + OFFLINE_CONTEXT_KEY, leaving the user with an empty app.
        if (authState.user?.id !== userId) {
            console.warn("[AuthDebug] fetchUserContext aborted: session changed mid-flight.");
            return;
        }

        if (error) {
            console.error("[AuthDebug] Failed to fetch memberships:", error);
            // Don't toast here, might be just network or empty
            return;
        }

        // [POISON GUARD] An empty result with no error frequently means RLS filtered
        // every row (auth degraded silently — JWT dead but request completed). Refuse
        // to overwrite a non-empty cache with empty data; legitimate "user has zero
        // memberships" cases self-correct on the next successful fetch.
        if (members && members.length === 0) {
            try {
                const existingCacheStr = localStorage.getItem(OFFLINE_CONTEXT_KEY);
                if (existingCacheStr) {
                    const existing = decodeSession(existingCacheStr);
                    if (existing?.userId === userId && existing?.members?.length > 0) {
                        console.warn("[AuthDebug] Refusing empty membership write — cache has data, suspected auth degradation.");
                        return;
                    }
                }
            } catch (e) { }
        }

        if (members) {
            members.forEach(m => {
                // 1. Cache Shop
                if (m.shop) {
                    const s = m.shop;
                    const shopModel = new Shop({
                        id: s.id,
                        name: s.name,
                        type: s.type,
                        location: s.location,
                        coordinates: coordinateUtils.parseCoordinates(s.coordinates),
                        website: s.website || '',
                        style: s.style,
                        calibration: s.calibration,
                        logo_url: s.logo_url || s.logo,
                        status: s.status,
                        venmo_handle: s.venmo_handle,
                        tipping_enabled: s.tipping_enabled,
                        allow_personal_tips: s.allow_personal_tips,
                        public_embed_token: s.public_embed_token,
                        mass_invite_token: s.mass_invite_token
                    });
                    coreState.shops.set(s.id, shopModel);
                }

                // 2. Cache Member
                const memberModel = new ShopMember({
                    id: m.id,
                    userId: m.user_id,
                    shopId: m.shop_id,
                    role: m.role,
                    status: m.status,
                    joinedAt: m.joined_at
                });
                coreState.members.set(m.id, memberModel);

                // 3. Set Context if first found (or restore from LocalStorage)
                // Filter for ACTIVE memberships only
                const activeMembers = members.filter(m => m.status && m.status.toLowerCase() === 'active');

                if (!coreState.currentShopId && activeMembers.length > 0) {
                    const storedShopId = localStorage.getItem('lewotobi_shop_id');
                    let targetId = storedShopId;

                    // Verify the stored ID belongs to an ACTIVE membership
                    const isValid = activeMembers.some(m => m.shop_id === storedShopId);
                    if (!isValid) {
                        targetId = null;
                    }

                    if (targetId) {
                        coreState.currentShopId = targetId;
                        const mem = activeMembers.find(m => m.shop_id === targetId);
                        coreState.currentUserRole = mem ? mem.role : 'Visitor';
                    } else {
                        // Fallback to first active found
                        coreState.currentShopId = activeMembers[0].shop_id;
                        coreState.currentUserRole = activeMembers[0].role;
                        localStorage.setItem('lewotobi_shop_id', coreState.currentShopId);
                    }
                }

                // 4. Update User Link
                if (coreState.users.has(userId)) {
                    const user = coreState.users.get(userId);
                    if (!user.shopId) user.shopId = coreState.currentShopId;
                }
            });
            console.log(`[AuthDebug] Context Hydrated. Shops: ${coreState.shops.size}, Members: ${coreState.members.size}`);

            // [POISON GUARD] Final session check before persisting to localStorage.
            // The forEach above could have yielded the event loop; if the session
            // dropped during processing, do not persist this snapshot.
            if (authState.user?.id !== userId) {
                console.warn("[AuthDebug] fetchUserContext aborted before cache write: session changed.");
                return;
            }

            // [RECONCILE] The fresh fetch is authoritative — prune any shops/members
            // hydrated from cache that the server didn't confirm. `m.shop` being null
            // is the shops-RLS filter ("no read access"), distinct from auth
            // degradation which is handled by the length-zero guard above. Without
            // this, an inactive owner of an archived shop keeps reading stale cache.
            const serverShopIds = new Set(members.filter(m => m.shop).map(m => m.shop.id));
            const serverMemberIds = new Set(members.map(m => m.id));
            for (const id of Array.from(coreState.shops.keys())) {
                if (!serverShopIds.has(id)) coreState.shops.delete(id);
            }
            for (const id of Array.from(coreState.members.keys())) {
                if (!serverMemberIds.has(id)) coreState.members.delete(id);
            }

            // Repair currentShopId if it no longer maps to an active membership.
            const activeMembers = members.filter(m => m.status && m.status.toLowerCase() === 'active');
            const currentStillActive = coreState.currentShopId
                && activeMembers.some(m => m.shop_id === coreState.currentShopId);
            if (!currentStillActive) {
                if (activeMembers.length > 0) {
                    coreState.currentShopId = activeMembers[0].shop_id;
                    coreState.currentUserRole = activeMembers[0].role;
                    localStorage.setItem('lewotobi_shop_id', coreState.currentShopId);
                } else {
                    coreState.currentShopId = null;
                    coreState.currentUserRole = null;
                    localStorage.removeItem('lewotobi_shop_id');
                }
            }

            // [OFFLINE CACHE] Dump context back to localStorage
            try {
                const contextDump = {
                    userId: userId,
                    shops: Array.from(coreState.shops.values()),
                    members: Array.from(coreState.members.values()),
                    currentShopId: coreState.currentShopId,
                    currentUserRole: coreState.currentUserRole
                };
                localStorage.setItem(OFFLINE_CONTEXT_KEY, encodeSession(contextDump));
            } catch (e) {
                console.warn("[AuthDebug] Failed to write offline context to cache", e);
            }
        }
    }

    async function _syncAvatar(userId, profile) {
        if (!profile.avatar) return; // Nothing to sync

        // Check if it's a Google Avatar (fast check)
        const isGoogle = profile.avatar.includes('googleusercontent.com');

        if (isGoogle) {
            console.log(`[Auth] Detected Google Avatar for ${userId}. Migrating to Storage...`);

            // Lazy verify: Do we already have it? (Optional optimization, maybe later)
            // For now, let's just do it. It's safe to overwrite / upsert.

            try {
                // 1. Convert URL to File
                const { storageUtils } = await import('../utils/storageUtils.js');
                const file = await storageUtils.urlToFile(profile.avatar, 'avatar_synced.png'); // Use generic name or preserve extension?

                if (!file) {
                    console.warn("[Auth] Failed to download Google avatar. Skipping sync.");
                    return;
                }

                // 2. Upload to 'avatars' bucket: {userId}/avatar.png
                // Using a fixed name ensures we don't pile up files. 'upsert: true' handles replacement.
                // We might want to use a timestamp query param for cache busting on the frontend if we update it.
                const fileName = `avatar_${Date.now()}.png`;
                const filePath = `${userId}/${fileName}`;

                const { error: uploadError } = await storageUtils.uploadFile('avatars', filePath, file);

                if (uploadError) {
                    console.error("[Auth] Avatar upload to storage failed:", uploadError);
                    return;
                }

                // 3. Get Public URL
                const publicUrl = storageUtils.getPublicUrl('avatars', filePath);

                // 4. Update Profile in DB
                const { error: dbError } = await supabase
                    .from('profiles')
                    .update({ avatar: publicUrl })
                    .eq('id', userId);

                if (dbError) {
                    console.error("[Auth] Start profile update failed:", dbError);
                    return;
                }

                // 5. Update Local State
                profile.avatar = publicUrl;
                coreState.activeUser.avatar = publicUrl; // Sync Legacy
                if (coreState.users.has(userId)) {
                    coreState.users.get(userId).avatar = publicUrl;
                }

                console.log("[Auth] Avatar successfully migrated to Supabase Storage:", publicUrl);

            } catch (err) {
                console.error("[Auth] Avatar sync process failed:", err);
            }
        }
    }

    function _createDraftProfile(userId) {
        if (authState.session && authState.session.user.id === userId) {
            const metadata = authState.session.user.user_metadata;
            const startName = metadata.full_name || metadata.name || 'New Diver';

            const draftUser = new Profile({
                id: userId,
                email: authState.session.user.email,
                name: startName,
                avatar: metadata.avatar_url || metadata.picture,
                languages: LanguageManager.inferFromUserMetadata(metadata),
                signupCompleted: false // Force false if profile row is missing. Stale metadata is dangerous.
            });
            authState.profile = draftUser;
            coreState.activeUser = draftUser;
        }
    }

    // [SAFE WAKE] Lightweight session check for App.vue focus/visibility events.
    // CRITICAL: Does NOT call supabase.auth.getSession() — that method hangs
    // when Chrome wakes a tab from passive state after memory pressure.
    // Instead, we check the in-memory session expiry and refresh directly if needed.
    let activeVerifyPromise = null;

    async function verifySession(forceHydration = false) {
        if (!authState.session) return;

        // 1. Singleton: Don't stack multiple verifications
        if (activeVerifyPromise) return activeVerifyPromise;

        // 2. Debounce: Skip if token was recently refreshed
        const lastEvt = authState.lastEventTimestamp || 0;
        if (!forceHydration && Date.now() - lastEvt < 10000) {
            console.log(`[Auth] Skipping verify (recently refreshed).`);
            return;
        }

        activeVerifyPromise = (async () => {
            const session = authState.session;
            if (!session) return;

            const now = Math.floor(Date.now() / 1000);
            const expiresAt = session.expires_at || 0;
            const timeLeft = expiresAt - now;

            // Token still valid and not expiring soon (> 5 min) — nothing to do
            if (timeLeft > 300) {
                console.log(`[Auth] Session OK (${timeLeft}s remaining).`);
                if (forceHydration) {
                    console.log(`[Auth] Forcing context hydration on valid session...`);
                    await fetchUserProfile(session.user.id);
                }
                return;
            }

            // Token expired or expiring soon — refresh it directly (no getSession!)
            console.log(`[Auth] Token ${timeLeft <= 0 ? 'expired' : 'expiring soon'} (${timeLeft}s). Refreshing...`);
            const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("Refresh Timeout")), 5000));

            try {
                // Race the internal gotrue lock against our 5s timeout
                const { data, error } = await Promise.race([supabase.auth.refreshSession(), timeout]);
                if (error || !data.session) {
                    const isNetworkError =
                        error?.message?.includes('Failed to fetch') ||
                        error?.message?.includes('NetworkError') ||
                        error?.status === 0 ||
                        error?.code === 'FetchError';

                    if (isNetworkError) {
                        console.warn("[AuthDebug] Background verify failed due to network. Trusting local session.", error);
                        if (forceHydration) {
                            await fetchUserProfile(session.user.id);
                        }
                        return; // Keep existing session
                    }

                    const isDefinitiveLogout = error?.message?.includes('Invalid Refresh Token') ||
                        error?.message?.includes('Invalid grant') ||
                        error?.status === 400 ||
                        error?.status === 401;

                    if (isDefinitiveLogout) {
                        if (navigator.onLine) {
                            console.error("[AuthDebug] Background verify definitive failure while ONLINE. Checking cross-tab recovery...", error);
                            
                            // [CROSS-TAB RECOVERY] Another tab or background process might have successfully refreshed it
                            try {
                                const offlineStr = localStorage.getItem(OFFLINE_KEY);
                                let offlineSession = decodeSession(offlineStr);
                                if (!offlineSession) offlineSession = _readRawGoTrueSession();
                                
                                if (offlineSession && offlineSession.access_token !== session?.access_token) {
                                    const sessionNow = Math.floor(Date.now() / 1000);
                                    if (offlineSession.expires_at && offlineSession.expires_at > sessionNow) {
                                        console.log("[AuthDebug] Found a newer valid session in offline cache. Recovering silently.");
                                        await _handleSessionUpdate(offlineSession);
                                        await supabase.auth.setSession(offlineSession);
                                        return;
                                    }
                                }
                            } catch (e) {
                                console.warn("[AuthDebug] Cross-tab recovery check failed:", e);
                            }
                            
                            _toast("Your session has expired. Please log in again.", "warning");
                            await logoutAuto();
                            return;
                        } else {
                            console.warn("[AuthDebug] Background verify definitive failure while OFFLINE. Preserving session.", error);
                            _toast("Session expired. Using offline data until you reconnect.", "warning");
                            return;
                        }
                    } else {
                        console.warn("[AuthDebug] Background verify ambiguous error. Trusting local session.", error);
                        if (forceHydration) {
                            await fetchUserProfile(session.user.id);
                        }
                        return;
                    }
                }
                console.log("[Auth] Session refreshed successfully.");
                authState.lastEventTimestamp = Date.now();
                await _handleSessionUpdate(data.session);
            } catch (err) {
                // [FIX] If the promise timed out, bypass gotrue and fetch manually
                if (err.message === "Refresh Timeout" && authState.session?.refresh_token) {
                    console.warn("[AuthDebug] Background verify lock stuck. Performing manual token refresh fallback...");
                    try {
                        const fallbackRes = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
                            method: 'POST',
                            headers: {
                                'apikey': supabaseAnonKey,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({ refresh_token: authState.session.refresh_token })
                        });

                        if (fallbackRes.ok) {
                            const data = await fallbackRes.json();
                            await supabase.auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token });
                            authState.lastEventTimestamp = Date.now();
                            if (forceHydration) {
                                await fetchUserProfile(session.user.id);
                            }
                            return;
                        } else {
                            if (navigator.onLine) {
                                console.error("[AuthDebug] Fallback fetch definitive rejection while ONLINE. Checking cross-tab recovery...");
                                
                                // [CROSS-TAB RECOVERY] Another tab or background process might have successfully refreshed it
                                try {
                                    const offlineStr = localStorage.getItem(OFFLINE_KEY);
                                    let offlineSession = decodeSession(offlineStr);
                                    if (!offlineSession) offlineSession = _readRawGoTrueSession();
                                    
                                    if (offlineSession && offlineSession.access_token !== session?.access_token) {
                                        const sessionNow = Math.floor(Date.now() / 1000);
                                        if (offlineSession.expires_at && offlineSession.expires_at > sessionNow) {
                                            console.log("[AuthDebug] Found a newer valid session in offline cache. Recovering silently.");
                                            await _handleSessionUpdate(offlineSession);
                                            await supabase.auth.setSession(offlineSession);
                                            return;
                                        }
                                    }
                                } catch (e) {
                                    console.warn("[AuthDebug] Cross-tab recovery check failed:", e);
                                }
                                
                                _toast("Your session has expired. Please log in again.", "warning");
                                await logoutAuto();
                                return;
                            } else {
                                console.warn("[AuthDebug] Fallback fetch definitive rejection while OFFLINE. Preserving session.");
                                _toast("Session expired. Using offline data until you reconnect.", "warning");
                                return;
                            }
                        }
                    } catch (fallbackErr) {
                        console.warn("[AuthDebug] Fallback fetch failed due to network. Trusting local session.");
                        if (forceHydration) {
                            await fetchUserProfile(session.user.id);
                        }
                        return;
                    }
                }
                console.warn("[AuthDebug] Background verify Promise Error:", err.message);
                if (forceHydration) {
                    console.log(`[Auth] Forcing context hydration on refreshed session...`);
                    await fetchUserProfile(session.user.id);
                }
            }
        })().finally(() => {
            activeVerifyPromise = null;
        });

        return activeVerifyPromise;
    }

    return {
        authState,
        initAuth,
        login,
        logout,
        fetchUserProfile,
        ensureSession,
        verifySession,
        handleAuthError
    };
}
